import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";
import Swal from "sweetalert2";
import { apiHost } from '../../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = (apiHost() || "").replace(/^https?:\/\//, "");
  return `${protocol}://${baseUrl}`;
};

const formatRupiah = (n) => {
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(Number(n) || 0);
};

const ConfirmGajiKaryawan = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const fetchMyGaji = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${getApiBaseUrl()}/gaji/my`, { withCredentials: true });
      setList(res.data?.data || []);
    } catch (e) {
      setSnackbar({ open: true, message: "Gagal memuat data gaji", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyGaji();
  }, []);

  const pendingList = list.filter((g) => g.status === "menunggu");

  const handleConfirm = async (row) => {
    const { isConfirmed } = await Swal.fire({
      title: "Konfirmasi Terima Gaji",
      text: `Anda yakin menerima slip gaji periode ${row.periode} (${formatRupiah(row.jumlah)})?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Ya, Terima",
    });
    if (!isConfirmed) return;
    setConfirming(true);
    try {
      await axios.put(`${getApiBaseUrl()}/gaji/${row.uuid}/confirm`, {}, { withCredentials: true });
      setSnackbar({ open: true, message: "Gaji berhasil dikonfirmasi diterima", severity: "success" });
      setDetailOpen(false);
      setSelected(null);
      fetchMyGaji();
    } catch (e) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Gagal konfirmasi", severity: "error" });
    } finally {
      setConfirming(false);
    }
  };

  const handleViewDetail = (row) => {
    setSelected(row);
    setDetailOpen(true);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Konfirmasi Terima Gaji
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Slip gaji yang belum Anda terima. Silakan periksa lalu klik "Terima Gaji" untuk mengonfirmasi.
      </Typography>

      <Card>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>
        ) : pendingList.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">Tidak ada slip gaji yang menunggu konfirmasi.</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Slip yang sudah diterima ada di menu "Data Gaji Saya".
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Periode</TableCell>
                  <TableCell>Tanggal</TableCell>
                  <TableCell align="right">Jumlah</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Aksi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingList.map((row) => (
                  <TableRow key={row.uuid}>
                    <TableCell>{row.periode}</TableCell>
                    <TableCell>{row.tanggal_periode ? new Date(row.tanggal_periode).toLocaleDateString("id-ID") : "-"}</TableCell>
                    <TableCell align="right">{formatRupiah(row.jumlah)}</TableCell>
                    <TableCell><Chip label={row.status} size="small" /></TableCell>
                    <TableCell align="center">
                      <Button size="small" startIcon={<VisibilityIcon />} onClick={() => handleViewDetail(row)} sx={{ mr: 1 }}>
                        Lihat
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleConfirm(row)}
                        disabled={confirming}
                      >
                        Terima Gaji
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Dialog detail + confirm */}
      <Dialog open={detailOpen} onClose={() => { setDetailOpen(false); setSelected(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Detail Slip Gaji</DialogTitle>
        <DialogContent>
          {selected && (
            <Card variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Periode</Typography>
              <Typography variant="body1" gutterBottom>{selected.periode}</Typography>
              <Typography variant="subtitle2" color="text.secondary">Tanggal</Typography>
              <Typography variant="body1" gutterBottom>
                {selected.tanggal_periode ? new Date(selected.tanggal_periode).toLocaleDateString("id-ID") : "-"}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">Gaji Pokok / Tambahan / Potongan</Typography>
              <Typography variant="body1" gutterBottom>
                {formatRupiah(selected.nominal)} / {formatRupiah(selected.tambahan)} / {formatRupiah(selected.potongan)}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">Jumlah Diterima</Typography>
              <Typography variant="h6">{formatRupiah(selected.jumlah)}</Typography>
              {selected.keterangan && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Keterangan</Typography>
                  <Typography variant="body2">{selected.keterangan}</Typography>
                </>
              )}
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDetailOpen(false); setSelected(null); }}>Tutup</Button>
          {selected && selected.status === "menunggu" && (
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleConfirm(selected)}
              disabled={confirming}
            >
              {confirming ? "Memproses..." : "Terima Gaji"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ConfirmGajiKaryawan;
